<script setup>
import { computed, ref, watch, defineProps, defineEmits } from "vue";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import ColorPicker from "primevue/colorpicker";
import Dropdown from "primevue/dropdown";
import { usePrintTheme, DEFAULT_PRINT_THEME, PRINT_FONT_OPTIONS } from "@/composables/usePrintTheme";
import { useLinkMetadata } from "@/composables/useLinkMetadata";
import { useAppToast } from "@/composables/useAppToast";

const props = defineProps({
  visible: Boolean,
});
const emit = defineEmits(["update:visible"]);

const { getPrintTheme, savePrintTheme } = usePrintTheme();
const { uploadImage } = useLinkMetadata();
const toast = useAppToast();

const loading = ref(false);
const saving = ref(false);
const saved = ref(false);
const uploading = ref(false);
const fileInput = ref(null);
const theme = ref({ ...DEFAULT_PRINT_THEME });

// El ColorPicker de PrimeVue trabaja con hex sin "#"; el resto de la app
// (y el CSS de la hoja impresa) usa hex con "#", así que se adapta acá.
const colorModel = (key) =>
  computed({
    get: () => (theme.value[key] || "").replace("#", ""),
    set: (v) => (theme.value[key] = v ? `#${v}` : ""),
  });

const accentColorModel = colorModel("accentColor");
const noteBgModel = colorModel("noteBg");
const noteColorModel = colorModel("noteColor");

watch(
  () => props.visible,
  async (visible) => {
    if (!visible) return;
    loading.value = true;
    try {
      theme.value = await getPrintTheme();
    } finally {
      loading.value = false;
    }
  }
);

const onFilePicked = async (event) => {
  const file = event.target.files?.[0];
  event.target.value = "";
  if (!file) return;
  uploading.value = true;
  try {
    const res = await uploadImage(file);
    if (res?.error) {
      toast.error("No se pudo subir el logo", res.error);
      return;
    }
    theme.value.logoUrl = res.image.url;
  } finally {
    uploading.value = false;
  }
};

const quitarLogo = () => {
  theme.value.logoUrl = "";
};

const save = async () => {
  saving.value = true;
  try {
    await savePrintTheme(theme.value);
  } catch (e) {
    console.error(e);
    toast.error("Ocurrió un error", "No se pudieron guardar los cambios.");
    saving.value = false;
    return;
  }
  saving.value = false;
  saved.value = true;
  setTimeout(() => (saved.value = false), 2500);
};
</script>

<template>
  <Dialog
    :visible="visible"
    @update:visible="(v) => emit('update:visible', v)"
    modal
    header="Impresión de salidas"
    :style="{ width: '480px' }"
  >
    <div class="text-sm text-500 mb-3">
      Personalizá los colores, la tipografía y el logo de la hoja de salida que se imprime para
      los publicadores.
    </div>

    <div v-if="loading" class="text-500 text-sm">Cargando…</div>

    <div v-else class="flex flex-column gap-4">
      <div class="flex align-items-center gap-3">
        <div
          class="logo-preview"
          :style="theme.logoUrl ? { backgroundImage: `url(${theme.logoUrl})` } : {}"
        >
          <i v-if="!theme.logoUrl" class="pi pi-image" />
        </div>
        <div class="flex flex-column gap-2">
          <div class="flex gap-2">
            <Button
              icon="pi pi-upload"
              label="Subir logo"
              size="small"
              outlined
              :loading="uploading"
              @click="fileInput?.click()"
            />
            <Button
              v-if="theme.logoUrl"
              icon="pi pi-times"
              label="Quitar"
              size="small"
              text
              severity="danger"
              @click="quitarLogo"
            />
          </div>
          <input
            ref="fileInput"
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            class="hidden-file-input"
            @change="onFilePicked"
          >
          <span class="text-xs text-500">Reemplaza el ícono por defecto en la hoja impresa</span>
        </div>
      </div>

      <div class="flex align-items-center justify-content-between">
        <label class="text-sm">Color de acento</label>
        <ColorPicker v-model="accentColorModel" />
      </div>

      <div class="flex align-items-center justify-content-between">
        <label class="text-sm">Fondo del recordatorio</label>
        <ColorPicker v-model="noteBgModel" />
      </div>

      <div class="flex align-items-center justify-content-between">
        <label class="text-sm">Texto del recordatorio</label>
        <ColorPicker v-model="noteColorModel" />
      </div>

      <div class="flex flex-column gap-2">
        <label class="text-sm">Tipografía</label>
        <Dropdown
          v-model="theme.fontFamily"
          :options="PRINT_FONT_OPTIONS"
          optionLabel="label"
          optionValue="value"
          class="w-full"
        />
      </div>

      <div class="print-preview" :style="{ fontFamily: theme.fontFamily || undefined }">
        <div class="print-preview-header" :style="{ borderBottomColor: theme.accentColor }">
          <img v-if="theme.logoUrl" :src="theme.logoUrl" alt="Logo" class="print-preview-logo">
          <span>Territorio a predicar</span>
        </div>
        <div
          class="print-preview-note"
          :style="{ background: theme.noteBg, color: theme.noteColor }"
        >
          Por favor, informá lo trabajado
        </div>
      </div>
    </div>

    <template #footer>
      <Button label="Cerrar" text @click="emit('update:visible', false)" />
      <Button
        :label="saved ? 'Guardado' : 'Guardar'"
        :icon="saved ? 'pi pi-check' : 'pi pi-save'"
        :loading="saving"
        :disabled="loading"
        @click="save"
      />
    </template>
  </Dialog>
</template>

<style scoped>
.hidden-file-input {
  display: none;
}

.logo-preview {
  width: 56px;
  height: 56px;
  border-radius: var(--radius-md);
  background: var(--bg-secondary);
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--label-tertiary);
  flex-shrink: 0;
}

.print-preview {
  border: 1px solid var(--border-color, #ddd);
  border-radius: var(--radius-md);
  padding: 12px;
  background: #fff;
  color: #111;
}

.print-preview-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  padding-bottom: 8px;
  border-bottom: 3px solid #007aff;
  margin-bottom: 8px;
}

.print-preview-logo {
  height: 20px;
  width: auto;
}

.print-preview-note {
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
}
</style>
